import Guid from "_shared/guids/guid";
import AHDate from "_shared/dates/date";

export interface Identifiable {
  id?: Guid | string | null;
}

export type Paginated<T> = {
  data: T[];
  headers: PaginationHeaders;
};

export default class PaginatedResponse<T extends Identifiable> {
  public static empty<T extends Identifiable>(): PaginatedResponse<T> {
    return new PaginatedResponse<T>([], {
      pageIndex: 0,
      pageSize: 0,
      totalElements: 0,
      totalPages: 0,
      hasPrev: false,
      hasNext: false,
      minCreatedDate: undefined,
      maxCreatedDate: undefined,
    });
  }

  public static parsePaginationHeader(headerValue: string): PaginationHeaders {
    const params = new URLSearchParams(headerValue);
    return {
      pageIndex: Number.parseInt(params.get("pageIndex") ?? "0", 10),
      pageSize: Number.parseInt(params.get("pageSize") ?? "0", 10),
      totalElements: Number.parseInt(params.get("totalElements") ?? "0", 10),
      totalPages: Number.parseInt(params.get("totalPages") ?? "0", 10),
      hasPrev: (params.get("hasPrev") ?? params.get("hasPrevious"))?.toLowerCase() === "true",
      hasNext: params.get("hasNext")?.toLowerCase() === "true",
      minCreatedDate: params.get("minCreatedDate") ?? undefined,
      maxCreatedDate: params.get("maxCreatedDate") ?? undefined,
    };
  }

  public static from<T extends Identifiable>(data: T[], headers: PaginationHeaders | string): PaginatedResponse<T> {
    if (typeof headers !== "string") {
      return new PaginatedResponse<T>(data, headers);
    }
    const params = PaginatedResponse.parsePaginationHeader(headers);
    return new PaginatedResponse<T>(data, {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      totalElements: params.totalElements,
      totalPages: params.totalPages,
      hasPrev: params.hasPrev,
      hasNext: params.hasNext,
      minCreatedDate: params.minCreatedDate,
      maxCreatedDate: params.maxCreatedDate,
    });
  }

  public readonly data: Array<T>;
  public readonly headers: PaginationHeaders;

  public get pageIndex(): number {
    return this.headers.pageIndex;
  }
  public get pageSize(): number {
    return this.headers.pageSize;
  }
  public get totalElements(): number {
    return this.headers.totalElements;
  }
  public get totalPages(): number {
    return this.headers.totalPages;
  }
  public get isEmpty(): boolean {
    return this.data.length === 0;
  }
  public get hasPreviousPage(): boolean {
    return this.headers.hasPrev ?? this.pageIndex > 0;
  }
  public get hasNextPage(): boolean {
    return this.headers.hasNext ?? this.pageIndex < this.totalPages - 1;
  }
  public get minCreatedDate(): AHDate {
    return new AHDate(this.headers.minCreatedDate);
  }
  public get maxCreatedDate(): AHDate {
    return new AHDate(this.headers.maxCreatedDate);
  }

  private constructor(data: Array<T>, headers: PaginationHeaders) {
    this.data = data;
    this.headers = headers;
  }

  public appendOrReplaceData(
    newData: T[],
    newHeaders?: PaginationHeaders,
    comparator?: (a: T, b: T) => number
  ): PaginatedResponse<T> {
    const getId = (item: T) => (typeof item.id === "string" ? item.id : (item.id as Guid)?.value);

    const existingDataMap = new Map<string, T>(this.data.map((item) => [getId(item), item]));

    let newCount = 0;
    for (const item of newData) {
      const id = getId(item);
      if (existingDataMap.has(id)) {
        // replace the old item
        existingDataMap.set(id, item);
      } else {
        // brand-new item
        existingDataMap.set(id, item);
        newCount++;
      }
    }

    const mergedData: T[] = Array.from(existingDataMap.values());

    if (comparator) {
      mergedData.sort(comparator);
    }

    if (newHeaders) {
      return new PaginatedResponse<T>(mergedData, { ...newHeaders });
    }

    const totalElements: number = this.totalElements + newCount;
    const totalPages: number = Math.ceil(totalElements / this.pageSize);

    return new PaginatedResponse<T>(mergedData, {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      totalElements,
      totalPages,
      hasPrev: this.hasPreviousPage,
      hasNext: this.hasNextPage,
      minCreatedDate: this.headers.minCreatedDate,
      maxCreatedDate: this.headers.maxCreatedDate,
    });
  }

  public removeDataById(id: Guid): PaginatedResponse<T> {
    const found: boolean = this.data.some((item) =>
      typeof item.id === "string" ? item.id === id.value : item.id?.isEqualTo(id)
    );
    const newData: T[] = this.data.filter(
      (item) => !(typeof item.id === "string" ? item.id === id.value : item.id?.isEqualTo(id))
    );
    const totalElements: number = this.totalElements - (found ? 1 : 0);
    const totalPages: number = Math.ceil(totalElements / this.pageSize);

    return new PaginatedResponse<T>(newData, {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      totalElements,
      totalPages,
      hasPrev: this.hasPreviousPage,
      hasNext: this.hasNextPage,
      minCreatedDate: this.headers.minCreatedDate,
      maxCreatedDate: this.headers.maxCreatedDate,
    });
  }
}

export type PaginationHeaders = {
  pageIndex: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  hasPrev?: boolean;
  hasNext?: boolean;
  minCreatedDate?: string;
  maxCreatedDate?: string;
};
